import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, PartyPopper, Sparkles } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { streamAI } from "../../lib/ai";
import { OCCASIONS, PACKAGES, SITE } from "../../content";
import { Reveal, Eyebrow } from "./Reveal";
import { Confetti } from "./Confetti";

const EMPTY = { name: "", email: "", phone: "", address: "", date: "", honoree: "", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-sm font-bold mb-2">{label}</span>
    {children}
  </label>
);

export const BookingForm = ({ pkg, setPkg, occasion, setOccasion }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [bookingId, setBookingId] = useState("");
  const [payLoading, setPayLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiIdeas, setAiIdeas] = useState([]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const today = new Date().toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", { ...form, occasion, package: pkg });
      setBookingId(data.id);
      setDone(true);
      setForm(EMPTY);
      toast.success("Request sent! A parent will reach out soon.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const payNow = async () => {
    setPayLoading(true);
    try {
      const lookupKey = PACKAGES.find((p) => p.name === pkg)?.lookupKey;
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        booking_id: bookingId,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPayLoading(false);
    }
  };

  const getIdeas = async () => {
    setAiLoading(true);
    setAiIdeas([]);
    try {
      const full = await streamAI("/ai/messages", { occasion, honoree: form.honoree }, () => {});
      const ideas = full.split("\n").map((l) => l.replace(/^\s*\d+[\).\s]*/, "").trim()).filter((l) => l.length > 2).slice(0, 5);
      setAiIdeas(ideas);
    } catch (e) {
      toast.error("Couldn't come up with ideas right now — try again!");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="py-24 md:py-32 bg-[var(--sky)] border-y-[3px] border-[var(--ink)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5 text-white">
          <Eyebrow color="var(--sun)"><span className="text-[var(--ink)]">Book a sign</span></Eyebrow>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[0.95]">
            Let's make someone's day.
          </h2>
          <p className="mt-5 text-base sm:text-lg leading-relaxed text-white/95 max-w-md">
            Send a request and a parent will contact you to confirm the details. Pay now to lock in your date, or wait for our call.
          </p>
          <div className="mt-10 space-y-2 font-semibold">
            <p data-testid="contact-email">Email: {SITE.email}</p>
            <p data-testid="contact-phone">Phone: {SITE.phone}</p>
            <p data-testid="contact-area">Serving: {SITE.area}</p>
            <p data-testid="contact-area-note" className="font-normal text-white/95 max-w-md pt-1">{SITE.areaNote}</p>
          </div>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <div className="relative sticker rounded-[32px] bg-[var(--cream)] p-6 sm:p-10">
            {done ? (
              <div data-testid="booking-success" className="relative text-center py-14 overflow-hidden">
                <Confetti />
                <PartyPopper className="mx-auto w-16 h-16 text-[var(--coral)]" />
                <h3 className="font-display font-bold text-4xl mt-6">Yay! Request received.</h3>
                <p className="mt-3 text-[var(--ink-soft)]">We'll get back to you soon. Thanks for helping us save!</p>
                {bookingId && (
                  <div data-testid="payment-panel" className="sticker-sm rounded-2xl bg-white mt-8 p-6 max-w-sm mx-auto text-left">
                    <p className="font-display font-semibold text-xl">Want to lock in your date?</p>
                    <p className="text-sm text-[var(--ink-soft)] mt-1">
                      {pkg} — {PACKAGES.find((p) => p.name === pkg)?.price} (pay now or later)
                    </p>
                    <motion.button
                      whileTap={{ scale: 0.96 }}
                      data-testid="stripe-pay-btn"
                      onClick={payNow}
                      disabled={payLoading}
                      className="mt-4 w-full sticker-sm rounded-full bg-[var(--grass)] text-white font-display font-semibold text-lg py-3 flex items-center justify-center gap-2 disabled:opacity-70"
                    >
                      {payLoading ? <Loader2 size={18} className="animate-spin" /> : null} Pay with card
                    </motion.button>
                    <p className="text-xs mt-3 text-[var(--ink-soft)]">Secure checkout by Stripe (test mode). Or just wait — a parent will confirm with you first.</p>
                  </div>
                )}
                <motion.button
                  whileTap={{ scale: 0.96 }}
                  data-testid="booking-another-btn"
                  onClick={() => setDone(false)}
                  className="mt-8 sticker-sm rounded-full bg-white px-6 py-3 font-display font-semibold"
                >
                  Book another sign
                </motion.button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="booking-form" className="grid sm:grid-cols-2 gap-5">
                <Field label="Your name"><input required data-testid="booking-name-input" className="field" value={form.name} onChange={set("name")} /></Field>
                <Field label="Email"><input required type="email" data-testid="booking-email-input" className="field" value={form.email} onChange={set("email")} /></Field>
                <Field label="Phone"><input required data-testid="booking-phone-input" className="field" value={form.phone} onChange={set("phone")} /></Field>
                <Field label="Date of the surprise"><input required type="date" min={today} data-testid="booking-date-input" className="field" value={form.date} onChange={set("date")} /></Field>
                <div className="sm:col-span-2">
                  <Field label="Delivery address"><input required data-testid="booking-address-input" className="field" value={form.address} onChange={set("address")} /></Field>
                </div>
                <Field label="Occasion">
                  <select data-testid="booking-occasion-select" className="field" value={occasion} onChange={(e) => setOccasion(e.target.value)}>
                    {OCCASIONS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Package">
                  <select data-testid="booking-package-select" className="field" value={pkg} onChange={(e) => setPkg(e.target.value)}>
                    {PACKAGES.map((p) => <option key={p.id}>{p.name}</option>)}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Who are we celebrating?"><input data-testid="booking-honoree-input" className="field" placeholder="e.g. Maya, turning 10" value={form.honoree} onChange={set("honoree")} /></Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Anything else? (message on the sign, colors, ideas)">
                    <textarea rows={3} data-testid="booking-message-input" className="field resize-none" value={form.message} onChange={set("message")} />
                  </Field>
                  <button
                    type="button"
                    data-testid="ai-ideas-btn"
                    onClick={getIdeas}
                    disabled={aiLoading}
                    className="mt-3 sticker-sm rounded-full bg-[var(--sun)] px-4 py-2 text-sm font-bold flex items-center gap-2 disabled:opacity-70"
                  >
                    {aiLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                    {aiLoading ? "Thinking up ideas..." : "Suggest message ideas"}
                  </button>
                  {aiIdeas.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3" data-testid="ai-ideas-list">
                      {aiIdeas.map((idea, i) => (
                        <button
                          type="button"
                          key={i}
                          data-testid={`ai-idea-${i}`}
                          onClick={() => setForm({ ...form, message: idea })}
                          className="sticker-sm rounded-full bg-white px-3 py-1.5 text-sm hover:bg-[var(--cream)]"
                        >
                          {idea}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <motion.button
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.97 }}
                  disabled={loading}
                  type="submit"
                  data-testid="booking-submit-btn"
                  className="sm:col-span-2 sticker rounded-full bg-[var(--coral)] text-white font-display font-semibold text-xl py-4 flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {loading && <Loader2 className="animate-spin" size={20} />} Send my request
                </motion.button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
